import { useSearchParams } from 'react-router-dom';
import { CalendarDays, List, LayoutTemplate } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import SocialPostsPage from '@/pages/marketing/SocialPostsPage';
import SocialCalendarPage from '@/pages/marketing/SocialCalendarPage';
import SocialPostTemplatesPage from '@/pages/marketing/SocialPostTemplatesPage';

const TABS = ['templates', 'posts', 'calendar'] as const;
type TabId = (typeof TABS)[number];

/**
 * The default tab, and therefore the one with NO ?tab= param.
 *
 * This moved from 'posts' to 'templates': an existing bookmark to
 * /marketing/social now lands on Templates. ?tab=posts still resolves to My
 * Posts, so nothing that named a tab explicitly breaks.
 */
const DEFAULT_TAB: TabId = 'templates';

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export default function SocialMediaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const active: TabId = isTabId(requested) ? requested : DEFAULT_TAB;

  const handleChange = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_TAB) {
      next.delete('tab');
    } else {
      next.set('tab', id);
    }
    setSearchParams(next, { replace: true });
  };

  const tabs = [
    {
      id: 'templates',
      label: 'Social Posts Templates',
      icon: <LayoutTemplate className="h-4 w-4" />,
      content: <div className="pt-4"><SocialPostTemplatesPage embedded /></div>,
    },
    {
      id: 'posts',
      label: 'My Posts',
      icon: <List className="h-4 w-4" />,
      content: <div className="pt-4"><SocialPostsPage embedded /></div>,
    },
    {
      id: 'calendar',
      label: 'Calendar',
      icon: <CalendarDays className="h-4 w-4" />,
      content: <div className="pt-4"><SocialCalendarPage embedded /></div>,
    },
  ];

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Social Media"
        subtitle="Reuse your corporate team's default posts, then publish or schedule across every social account connected in Zernio."
      />
      <Tabs key={active} tabs={tabs} defaultTab={active} onChange={handleChange} />
    </div>
  );
}
